import {
  CANONICAL_SOURCE_FALLBACKS,
  canonicalDatasetUrl,
  deprecatedIdForCanonical,
  resolveDatasetUrl,
  resolveSourceId,
  warnDeprecatedSourceId,
} from "./sourceAliases.js";
import type {
  ApiIndex,
  BasicAuthCredentials,
  ChartsCatalog,
  ClientFetchOptions,
  SourceDefinition,
  SourcesCatalog,
} from "./types.js";

export const PUBLISHED_CHART_ORIGIN = "https://charts.n10assets.com/";

/** Join base URL with a relative or absolute path. */
export function joinUrl(base: string, path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const b = base.replace(/\/$/, "");
  const p = path.replace(/^\//, "");
  return `${b}/${p}`;
}

/** Build an `Authorization` header value for HTTP Basic auth. */
export function basicAuthAuthorization(credentials: BasicAuthCredentials): string {
  const token = `${credentials.username}:${credentials.password}`;
  const encoded =
    typeof globalThis.btoa === "function"
      ? globalThis.btoa(token)
      : Buffer.from(token, "utf8").toString("base64");
  return `Basic ${encoded}`;
}

function fetchInit(options?: ClientFetchOptions): RequestInit | undefined {
  if (!options?.basicAuth) {
    return undefined;
  }
  return {
    headers: {
      Authorization: basicAuthAuthorization(options.basicAuth),
    },
  };
}

/** Query parameter appended to defeat a stale browser cache. */
export const CACHE_BUST_PARAM = "v";

/**
 * Fixed once per page load, so every chart on a page requests the same URLs and the
 * browser still serves the second chart's catalog read from its own cache.
 *
 * Why this exists at all: objects were served for months with no `Cache-Control`, so
 * browsers applied *heuristic* freshness and stored copies that they will reuse for days
 * without revalidating. A CloudFront invalidation cannot reach those — it clears
 * CloudFront only. Changing the URL is the one lever that does, because the browser cache
 * is keyed by the full URL including the query string.
 *
 * Deliberately not a per-request timestamp: a stable token means normal `Cache-Control`
 * semantics still apply within a page session (revalidate, cheap 304s). The token's job is
 * to escape already-poisoned entries, not to defeat caching forever. CloudFront's cache
 * policy excludes query strings from its cache key, so this costs no extra origin traffic.
 */
const PAGE_LOAD_CACHE_TOKEN = Date.now().toString(36);

function cacheBustToken(option: ClientFetchOptions["cacheBust"]): string | undefined {
  if (option === false) {
    return undefined;
  }
  if (typeof option === "string") {
    return option.trim() || undefined;
  }
  return PAGE_LOAD_CACHE_TOKEN;
}

/**
 * Append the cache-busting token to `url`. Works for absolute and relative URLs, preserves
 * any existing query string and fragment, and leaves a URL that already carries the
 * parameter untouched so a caller versioning its own URLs stays in control.
 */
export function withCacheBust(url: string, token?: string): string {
  if (!token) {
    return url;
  }
  const hashAt = url.indexOf("#");
  const bare = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  if (new RegExp(`[?&]${CACHE_BUST_PARAM}=`).test(bare)) {
    return url;
  }
  const separator = bare.includes("?") ? "&" : "?";
  return `${bare}${separator}${CACHE_BUST_PARAM}=${encodeURIComponent(token)}${hash}`;
}

/**
 * Narrow chart mount options down to the fetch-layer options, preserving the existing
 * "undefined when nothing is set" contract so callers that pass neither are unaffected.
 */
export function clientFetchOptionsFrom(options: {
  basicAuth?: BasicAuthCredentials;
  cacheBust?: boolean | string;
}): ClientFetchOptions | undefined {
  const resolved: ClientFetchOptions = {};
  if (options.basicAuth) {
    resolved.basicAuth = options.basicAuth;
  }
  if (options.cacheBust !== undefined) {
    resolved.cacheBust = options.cacheBust;
  }
  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

/** Low-level `fetch` with optional Basic auth (for callers that handle status themselves). */
export async function fetchWithClientOptions(
  url: string,
  options?: ClientFetchOptions
): Promise<Response> {
  return fetch(withCacheBust(url, cacheBustToken(options?.cacheBust)), fetchInit(options));
}

export async function fetchSourcesCatalog(
  catalogUrl: string,
  options?: ClientFetchOptions
): Promise<SourcesCatalog> {
  const res = await fetchWithClientOptions(catalogUrl, options);
  if (!res.ok) {
    throw new Error(`nten-data: failed to load sources catalog (${res.status}): ${catalogUrl}`);
  }
  return (await res.json()) as SourcesCatalog;
}

export async function fetchChartsCatalog(
  catalogUrl: string,
  options?: ClientFetchOptions
): Promise<ChartsCatalog> {
  const res = await fetchWithClientOptions(catalogUrl, options);
  if (!res.ok) {
    throw new Error(`nten-data: failed to load charts catalog (${res.status}): ${catalogUrl}`);
  }
  return (await res.json()) as ChartsCatalog;
}

export async function fetchApiIndex(indexUrl: string, options?: ClientFetchOptions): Promise<ApiIndex> {
  const res = await fetchWithClientOptions(indexUrl, options);
  if (!res.ok) {
    throw new Error(`nten-data: failed to load API index (${res.status}): ${indexUrl}`);
  }
  return (await res.json()) as ApiIndex;
}

export function findSource(catalog: SourcesCatalog, id: string): SourceDefinition | undefined {
  const canonical = resolveSourceId(id);
  warnDeprecatedSourceId(id, canonical);

  const byCanonical = catalog.sources.find((s) => s.id === canonical);
  if (byCanonical) {
    return byCanonical;
  }

  const byRowAlias = catalog.sources.find(
    (s) => s.aliases?.includes(canonical) || s.aliases?.includes(id)
  );
  if (byRowAlias) {
    return byRowAlias;
  }

  // Legacy deployed catalogs whose primary id is still deprecated (e.g. bar-chart-demo).
  const legacyId = deprecatedIdForCanonical(canonical);
  if (legacyId) {
    const byLegacy = catalog.sources.find((s) => s.id === legacyId);
    if (byLegacy) {
      return byLegacy;
    }
  }

  const targetUrl = canonicalDatasetUrl(canonical);
  const byDatasetUrl = catalog.sources.find((s) => resolveDatasetUrl(s.url) === targetUrl);
  if (byDatasetUrl) {
    return byDatasetUrl;
  }

  const byRawId = catalog.sources.find((s) => s.id === id);
  if (byRawId) {
    return byRawId;
  }

  return CANONICAL_SOURCE_FALLBACKS[canonical];
}

/** Add stub catalog rows for canonical sources missing from an older `sources.json`. */
export function enrichSourcesCatalog(catalog: SourcesCatalog): SourcesCatalog {
  const seen = new Set(catalog.sources.map((s) => s.id));
  const extra = Object.values(CANONICAL_SOURCE_FALLBACKS).filter((s) => !seen.has(s.id));
  if (!extra.length) {
    return catalog;
  }
  return { sources: [...catalog.sources, ...extra] };
}

export async function fetchJson<T = unknown>(url: string, options?: ClientFetchOptions): Promise<T> {
  const res = await fetchWithClientOptions(url, options);
  if (!res.ok) {
    throw new Error(`nten-data: failed to load data (${res.status}): ${url}`);
  }
  return (await res.json()) as T;
}

export function defaultBaseUrl(): string {
  return PUBLISHED_CHART_ORIGIN;
}

/**
 * When callers override the sources catalog URL for local preview/testing, resolve
 * relative dataset paths from that catalog location. Otherwise use the published origin.
 */
export function resolveCatalogBaseUrl(catalogUrl?: string): string {
  if (!catalogUrl) {
    return defaultBaseUrl();
  }
  try {
    const fallbackBase =
      typeof window !== "undefined" && window.location?.href
        ? window.location.href
        : `${defaultBaseUrl()}/`;
    const resolvedCatalog = new URL(catalogUrl, fallbackBase);
    return new URL(".", resolvedCatalog).href.replace(/\/$/, "");
  } catch {
    return defaultBaseUrl();
  }
}
